// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { LazyPicture } from "@werner/MobiusOverrides/LazyImage";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    largeImagePath = "largeImagePath",
    mediumImagePath = "mediumImagePath",
    smallImagePath = "smallImagePath",
    altText = "altText",
    imageLink = "imageLink",
    width = "width",
    height = "height",
    openInNewTab = "openInNewTab",
    horizontalAlignment = "horizontalAlignment",
    disableLazyLoad = "disableLazyLoad",
    trackImageLinkClick = "trackImageLinkClick",
    imageLinkClickEventName = "imageLinkClickEventName",
    imageLinkClickEventClickText = "imageLinkClickEventClickText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.largeImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.smallImagePath]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.width]: string;
        [fields.height]: string;
        [fields.horizontalAlignment]: string;
        [fields.disableLazyLoad]: boolean;
        [fields.trackImageLinkClick]: boolean;
        [fields.imageLinkClickEventName]: string;
        [fields.imageLinkClickEventClickText]: string;
    };
}

// Xng-Change
const mapStateToProps = (state: xGoogleAnalyticsState) => ({
    xGoogleAnalytics: state.xGoogleAnalytics,
    isAuthenticated: xIsAuthenticated(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ImageStyles {
    wrapper?: InjectableCss;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    wrapper: {
        css: css`
            img {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
};

const styles = imageStyles;

const Image: FC<Props> = ({ fields, xGoogleAnalytics, isAuthenticated }) => {
    const { url } = useGetLink(fields.imageLink);
    const image = (
        <LazyPicture
            smallImageSrc={fields.smallImagePath}
            mediumImageSrc={fields.mediumImagePath}
            largeImageSrc={fields.largeImagePath}
            altText={fields.altText}
            disableLazyLoad={fields.disableLazyLoad}
            width={fields.width}
            height={fields.height}
            css={css`
                ${fields.width &&
                css`
                    & > picture {
                        width: ${fields.width};
                        img {
                            width: ${fields.width};
                        }
                    }
                `}
            `}
        />
    );

    // Xng-Change
    // WES-1775
    const sendTracking = () => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.socialButtonsClickEvent) {
            return;
        }

        const googleAnalyticsData = {
            event: fields.imageLinkClickEventName,
            click_text: fields.imageLinkClickEventClickText,
            destination: url,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper
            css={css`
                ${fields.width &&
                css`
                    width: ${fields.width};
                `}
                ${fields.height &&
                css`
                    height: ${fields.height};
                `}
                ${!!fields.horizontalAlignment &&
                css`
                    display: flex;
                    justify-content: ${fields.horizontalAlignment};
                `}
                
                && > div:first-child {
                    height: 100%;
                }
                img {
                    max-width: 100%;
                    max-height: ${fields.height ? `${fields.height}px` : "100%"};
                }
            `}
            className="img-wrapper"
        >
            {url ? (
                <Clickable
                    href={url}
                    target={fields.openInNewTab ? "_blank" : "_self"}
                    {...styles.imageLink}
                    aria-label={`${fields.altText}`}
                    onClick={sendTracking}
                >
                    {image}
                </Clickable>
            ) : (
                image
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Image),
    definition: {
        group: "XNGAGE" as any,
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },

            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openInNewTab,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.width,
                fieldType: "General",
                editorTemplate: "TextField",
                placeholder: "Enter: width style",
                defaultValue: "",
                validate: (value: string | null) => {
                    return !value ? null : CSS.supports("width", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.height,
                fieldType: "General",
                editorTemplate: "TextField",
                placeholder: "Enter: height style",
                defaultValue: "",
                validate: (value: string | null) => {
                    return !value ? null : CSS.supports("height", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.horizontalAlignment,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.disableLazyLoad,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.trackImageLinkClick,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.imageLinkClickEventName,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: item => item.fields.trackImageLinkClick,
            },
            {
                name: fields.imageLinkClickEventClickText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: item => item.fields.trackImageLinkClick,
            },
        ],
    },
};

export default widgetModule;
